import { IntegrationType, QuickbooksService, ZendeskService } from '@/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export type DeleteWorkspaceConfirmationProps = {
  isOpen: boolean;
  handleOpen: (boolean: boolean) => void;
  integrationType: IntegrationType;
};

export const RevokeQuickbooksAccessTokenConfirmation = ({
  isOpen,
  handleOpen,
  integrationType,
}: DeleteWorkspaceConfirmationProps) => {
  const queryClient = useQueryClient();
  const org = useStore($org);
  // mutation to revoke an access Token
  const revokeQuickbooksAuthToken = useMutation({
    mutationFn: async () => {
      // call the api to revoke zendesk auth token
      if (!org) return;
      const res = await QuickbooksService.revokeQuickbooksAuthorizationToken(org?.id);
      if (!res.success) {
        throw new Error('Failed to revoke Quickbooks token');
      }
      return res;
    },
    onSuccess: async data => {
      // invalidate queries here so that they are refetched
      await queryClient.invalidateQueries({
        queryKey: ['getIntegrations', org?.id],
      });
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone, and you will not be able to reconnect
            to Quickbooks with the same token.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={async e => {
              e.preventDefault();
              if (integrationType === 'Quickbooks') {
                await revokeQuickbooksAuthToken.mutateAsync();
              }
              handleOpen(false);
            }}
            disabled={revokeQuickbooksAuthToken.isPending}
            className="bg-red-600 hover:bg-red-500 dark:text-white dark:hover:bg-red-700"
          >
            {revokeQuickbooksAuthToken.isPending ? 'Processing...' : 'Continue'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
